import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  adminListAllowlist,
  adminAddEmail,
  adminRemoveEmail,
  adminUsersActivity,
  adminUserDetail,
  adminViewAs,
} from "@/lib/journeyApi";
import { api } from "@/lib/api";
import { toast } from "sonner";
import buildInfo from "@/buildInfo.json";

export default function Admin() {
  const { user, checkAuth } = useAuth();
  const [allowlist, setAllowlist] = useState([]);
  const [users, setUsers] = useState([]);
  const [keyStatus, setKeyStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Drill-down state: { [user_id]: { open, loading, detail } } — keyed
  // so several users can be expanded at once without clobbering each
  // other.
  const [drillState, setDrillState] = useState({});
  // Which user row is mid-View-As switch (button shows "Switching…").
  const [viewingAsId, setViewingAsId] = useState(null);

  const isAdmin = !!user?.is_admin;

  const startViewAs = useCallback(async (u) => {
    if (!window.confirm(
      `View the app as ${u.email}?\n\n` +
      "All writes will be blocked while you're in view-as mode. " +
      "Use the red banner at the top to exit."
    )) return;
    setViewingAsId(u.user_id);
    try {
      await adminViewAs(u.user_id);
      // Refresh auth context so the banner appears immediately, then
      // bounce to /journey — that's the first thing the impersonated
      // user would see when they log in.
      await checkAuth();
      toast.success(`Now viewing as ${u.email}`);
      window.location.href = "/journey";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start view-as");
      setViewingAsId(null);
    }
  }, [checkAuth]);

  const toggleDrill = useCallback(async (u) => {
    const uid = u.user_id;
    const current = drillState[uid];
    if (current?.open) {
      // collapse
      setDrillState((s) => ({ ...s, [uid]: { ...current, open: false } }));
      return;
    }
    // expand — fetch if not already cached
    setDrillState((s) => ({
      ...s, [uid]: { open: true, loading: !current?.detail, detail: current?.detail },
    }));
    if (!current?.detail) {
      try {
        const detail = await adminUserDetail(uid);
        setDrillState((s) => ({ ...s, [uid]: { open: true, loading: false, detail } }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't load user detail");
        setDrillState((s) => ({ ...s, [uid]: { open: false, loading: false } }));
      }
    }
  }, [drillState]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, u, k] = await Promise.all([
        adminListAllowlist(),
        adminUsersActivity(),
        api.get("/admin/youtube-keys").then((r) => r.data).catch(() => null),
      ]);
      setAllowlist(a);
      setUsers(u);
      setKeyStatus(k);
    } catch {
      toast.error("Could not load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [load, isAdmin]);

  if (!user) return null;
  if (!isAdmin) return <Navigate to="/journey" replace />;

  const handleAdd = async (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) return;
    setSubmitting(true);
    try {
      await adminAddEmail(v);
      setEmail("");
      await load();
      toast.success(`Authorized ${v}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add email");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (em) => {
    if (!window.confirm(`Revoke access for ${em}?`)) return;
    try {
      await adminRemoveEmail(em);
      await load();
      toast.success("Removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div data-testid="admin-page" className="px-6 lg:px-10 py-8 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400">
          // ADMIN CONSOLE
        </div>
        {/* Build stamp — git SHA + build timestamp baked in at deploy.
            If this number matches what you expect, you're on the latest
            production build. If not, hard-refresh / clear cache. */}
        <div
          data-testid="admin-build-version"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 select-all"
          title={`Built at ${buildInfo.builtAt}`}
        >
          BUILD <span className="text-yellow-400">{buildInfo.version}</span>
          <span className="text-zinc-700 mx-2">·</span>
          {buildInfo.builtAtLabel}
        </div>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        Access control
      </h1>
      <p className="text-zinc-400 text-sm max-w-xl mb-10 leading-relaxed">
        Only Gmail addresses on this allowlist can sign in. You're permanently
        authorized as the admin.
      </p>

      {/* YouTube API key health */}
      {keyStatus && (
        <div
          data-testid="yt-keys-section"
          className="bg-zinc-950 border border-zinc-800 rounded-sm p-5 mb-10 grid sm:grid-cols-3 gap-4"
        >
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-1">
              YouTube API keys
            </div>
            <div className="font-display text-3xl font-black text-white">
              {keyStatus.active_today} <span className="text-zinc-500 text-base font-normal">/ {keyStatus.total_configured} active</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Daily quota: 10,000 units per key · resets midnight Pacific
            </div>
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-1">
              Exhausted today
            </div>
            {keyStatus.exhausted_today.length === 0 ? (
              <div className="text-emerald-300 text-sm font-mono">none — all keys healthy</div>
            ) : (
              <div className="flex flex-wrap gap-1 mt-1">
                {keyStatus.exhausted_today.map((tail) => (
                  <span key={tail} className="font-mono text-[10px] bg-red-500/10 border border-red-500/40 text-red-300 px-2 py-0.5 rounded-sm">
                    …{tail}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-1">
              Add more keys
            </div>
            <div className="text-[11px] text-zinc-300 leading-relaxed">
              Set <span className="font-mono text-yellow-400">YOUTUBE_API_KEY_2</span>,{" "}
              <span className="font-mono text-yellow-400">_3</span>, etc. in
              backend .env. Each adds 10k daily quota. Redeploy after editing.
            </div>
          </div>
        </div>
      )}

      {/* Add email card */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-10">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 mb-3">
          // AUTHORIZE A NEW USER
        </div>
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="admin-add-email-input"
            type="email"
            placeholder="someone@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-1 bg-black border border-zinc-800 rounded-sm px-4 py-3 text-white font-mono text-sm focus:outline-none focus:border-yellow-400"
          />
          <button
            data-testid="admin-add-email-btn"
            type="submit"
            disabled={submitting}
            className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors disabled:opacity-60"
          >
            {submitting ? "Adding…" : "▸ Authorize"}
          </button>
        </form>
      </div>

      {/* Allowlist table */}
      <section className="mb-12">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-2xl font-bold text-white">
            Authorized emails
          </h2>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            {allowlist.length} total
          </span>
        </div>
        {loading ? (
          <div className="font-mono text-sm text-zinc-500">
            [recon] loading<span className="blink">_</span>
          </div>
        ) : (
          <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="allowlist-table">
            <div className="grid grid-cols-12 px-4 py-3 bg-zinc-950 border-b border-zinc-800 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
              <div className="col-span-6">Email</div>
              <div className="col-span-3">Added by</div>
              <div className="col-span-2">Added</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            {allowlist.map((row) => {
              const isAdminRow = row.email === user.email;
              return (
                <div
                  key={row.email}
                  data-testid={`allowlist-row-${row.email}`}
                  className="grid grid-cols-12 px-4 py-3 border-b border-zinc-900 last:border-b-0 items-center"
                >
                  <div className="col-span-6 text-sm text-white font-mono truncate">
                    {row.email}
                    {isAdminRow && (
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-yellow-400 text-yellow-400 rounded-sm">
                        ADMIN
                      </span>
                    )}
                  </div>
                  <div className="col-span-3 font-mono text-xs text-zinc-400 truncate">
                    {row.added_by}
                  </div>
                  <div className="col-span-2 font-mono text-xs text-zinc-500">
                    {new Date(row.added_at).toLocaleDateString()}
                  </div>
                  <div className="col-span-1 text-right">
                    {!isAdminRow && (
                      <button
                        data-testid={`allowlist-remove-${row.email}`}
                        onClick={() => handleRemove(row.email)}
                        className="font-mono text-xs uppercase tracking-wider text-zinc-500 hover:text-red-400"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* User activity table */}
      <section data-testid="user-activity-section">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-2xl font-bold text-white">
            User activity
          </h2>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            {users.length} signed in
          </span>
        </div>
        <p className="text-xs text-zinc-500 mb-4 leading-relaxed max-w-2xl">
          Who's accessed the system, when they were last seen, and what they've
          done so far. Sorted by most-recently active.
        </p>

        {users.length === 0 ? (
          <div className="border border-zinc-800 rounded-sm p-6 font-mono text-sm text-zinc-500 text-center">
            No users have signed in yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="user-activity-list">
            {users.map((u) => (
              <div
                key={u.user_id}
                data-testid={`activity-row-${u.email}`}
                className="border border-zinc-800 rounded-sm p-4 bg-zinc-950/40"
              >
                <div className="flex items-start gap-4 mb-4">
                  {u.picture ? (
                    <img src={u.picture} alt={u.name} className="h-10 w-10 rounded-full" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-zinc-800" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-semibold truncate">{u.name || u.email}</span>
                      <span
                        className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${
                          u.is_admin
                            ? "border-yellow-400 text-yellow-400"
                            : "border-zinc-700 text-zinc-400"
                        }`}
                      >
                        {u.is_admin ? "ADMIN" : "USER"}
                      </span>
                    </div>
                    <div className="font-mono text-xs text-zinc-400 truncate">{u.email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Last seen
                    </div>
                    <div className="text-xs text-zinc-300">
                      {u.last_seen_at
                        ? new Date(u.last_seen_at).toLocaleString()
                        : "never"}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-1">
                      joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                </div>

                {/* Journey progress bar */}
                <div className="mb-3">
                  <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                    <span>Creator journey progress</span>
                    <span>
                      {u.journey_done}/{u.journey_total} · {u.journey_pct}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 transition-all"
                      style={{ width: `${Math.min(100, u.journey_pct)}%` }}
                    />
                  </div>
                </div>

                {/* Activity stats grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-2">
                  <Stat label="Scans" value={`${u.scans_complete} / ${u.scans_total}`} />
                  <Stat label="Saved Niches" value={u.saved_channels ?? u.niches_saved} hi={(u.saved_channels ?? u.niches_saved) > 0} />
                  <Stat label="Saved Kits" value={u.saved_kits ?? 0} hi={(u.saved_kits ?? 0) > 0} />
                  <Stat label="Channels created" value={u.channel_creations} hi={u.channel_creations > 0} />
                  <Stat label="Storyboards" value={u.storyboards ?? 0} hi={(u.storyboards ?? 0) > 0} />
                  <Stat label="Story ideas" value={u.saved_story_ideas ?? 0} hi={(u.saved_story_ideas ?? 0) > 0} />
                  <Stat label="Journey" value={`${u.journey_pct}%`} />
                  <Stat label="Sessions" value={u.sessions_active} />
                </div>
                {u.last_scan_at && (
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                    Last scan {new Date(u.last_scan_at).toLocaleString()}
                  </div>
                )}

                {/* Drill-down toggle — admin can peek at exact saved
                    items per user without opening the database. */}
                <div className="mt-4 pt-3 border-t border-zinc-900">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                      type="button"
                      data-testid={`activity-drill-toggle-${u.email}`}
                      onClick={() => toggleDrill(u)}
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 hover:text-yellow-300"
                    >
                      {drillState[u.user_id]?.open ? "▼ hide details" : "▸ view saved items"}
                    </button>
                    {/* "View as" — start a read-only impersonation
                        session. Hidden for the admin's own row and
                        for fellow admins so we don't tangle two
                        admin sessions. */}
                    {!u.is_admin && u.user_id !== user?.user_id && (
                      <button
                        type="button"
                        data-testid={`view-as-btn-${u.email}`}
                        onClick={() => startViewAs(u)}
                        disabled={viewingAsId === u.user_id}
                        className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-red-500/60 text-red-300 hover:bg-red-500/20 hover:text-red-200 disabled:opacity-50 rounded-sm transition-colors"
                        title={`Open the app as ${u.email} (read-only)`}
                      >
                        {viewingAsId === u.user_id ? "Switching…" : "↻ View as user"}
                      </button>
                    )}
                  </div>
                  {drillState[u.user_id]?.open && (
                    <UserDrillDown
                      userId={u.user_id}
                      loading={drillState[u.user_id]?.loading}
                      detail={drillState[u.user_id]?.detail}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, hi = false }) {
  return (
    <div className="border border-zinc-900 rounded-sm px-3 py-2 bg-black">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {label}
      </div>
      <div className={`font-display text-lg font-bold ${hi ? "text-yellow-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}


/**
 * UserDrillDown — inline detail panel under each user row showing
 * the EXACT items they've saved (newest first, up to 50 per list):
 * saved niches, saved channel kits, channels they've created,
 * storyboards, and story ideas. Used by admin to investigate
 * activity without touching the database.
 */
// Compact "2h ago" / "Jan 4" relative date — short enough to fit
// in a drill-down row without breaking the layout. Returns "" if no
// date is parseable so we can simply skip rendering.
function _fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function _fmtAbs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

// Pill showing the relative + absolute date on hover. Tiny + monospace
// so it doesn't compete with the row's primary title.
function WhenPill({ iso }) {
  const rel = _fmtWhen(iso);
  if (!rel) return null;
  return (
    <span
      className="font-mono text-[9px] text-zinc-500 whitespace-nowrap shrink-0"
      title={_fmtAbs(iso)}
    >
      {rel}
    </span>
  );
}

function UserDrillDown({ userId, loading, detail }) {
  if (loading) {
    return (
      <div data-testid={`activity-drill-loading-${userId}`} className="mt-3 font-mono text-[11px] text-zinc-500">
        [recon] loading saved items<span className="blink">_</span>
      </div>
    );
  }
  if (!detail) return null;
  const lists = [
    { key: "saved_niches", label: "Saved Niches", items: detail.saved_niches, render: (n) => (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-white truncate flex-1">{n.name}</span>
          <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
            {n.niche || "—"}{n.subscribers ? ` · ${n.subscribers.toLocaleString()} subs` : ""}
          </span>
          <WhenPill iso={n.saved_at} />
        </div>
      ) },
    { key: "saved_kits", label: "Saved Kits", items: detail.saved_kits, render: (k) => (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-white truncate flex-1">{k.name || k.kit_id}</span>
          <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">{k.topic || "—"}</span>
          <WhenPill iso={k.saved_at || k.created_at} />
        </div>
      ) },
    { key: "channel_creations", label: "Channel Kits Created", items: detail.channel_creations, render: (c) => (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-white truncate flex-1">{c.name || c.job_id}</span>
          <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
            {c.handle || ""}{c.source_niche ? ` · FROM ${c.source_niche}` : ""}
          </span>
          <span className={`font-mono text-[9px] uppercase tracking-wider shrink-0 ${
            c.status === "complete" ? "text-emerald-300" : c.status === "failed" ? "text-red-300" : "text-yellow-400"
          }`}>
            {c.status}
          </span>
          <WhenPill iso={c.created_at} />
        </div>
      ) },
    { key: "storyboards", label: "Storyboards", items: detail.storyboards, render: (s) => {
        const sceneCount = s.scene_count ?? 0;
        const collabs = Array.isArray(s.collaborators) ? s.collaborators : [];
        const channelTag = s.channel_handle || s.channel_id || "";
        // Tooltip text for the "+N shared" pill — list every
        // collaborator email + role so the admin can verify exactly
        // who else has access to this storyboard without leaving the
        // page.
        const collabTitle = collabs
          .map((c) => `${c.email || "?"} (${c.role || "editor"})`)
          .join("\n");
        const row = (
          <div className="space-y-0.5">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-white truncate flex-1">{s.title || s.video_name || s.storyboard_id}</span>
              <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                {sceneCount} {sceneCount === 1 ? "scene" : "scenes"} · {s.status || "—"}
              </span>
              <WhenPill iso={s.updated_at || s.created_at} />
            </div>
            <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-600">
              {channelTag && (
                <span className="truncate" title={`channel_id: ${s.channel_id || "—"}`}>
                  → {channelTag}
                </span>
              )}
              {collabs.length > 0 && (
                <span
                  className="text-amber-400 whitespace-nowrap"
                  title={`Collaborators:\n${collabTitle}`}
                  data-testid={`admin-storyboard-collab-${s.storyboard_id}`}
                >
                  +{collabs.length} shared
                </span>
              )}
              <span className="ml-auto truncate opacity-60" title={`storyboard_id: ${s.storyboard_id}`}>
                {(s.storyboard_id || "").slice(0, 14)}
              </span>
            </div>
          </div>
        );
        // Same Link affordance we use for automation_jobs — admins
        // can click straight into the storyboard (read-only bypass).
        if (s.storyboard_id) {
          return (
            <Link
              to={`/storyboards/${encodeURIComponent(s.storyboard_id)}`}
              data-testid={`admin-storyboard-link-${s.storyboard_id}`}
              className="block hover:bg-zinc-900/60 -mx-2 px-2 py-1 rounded-sm transition-colors"
              title="Open this user's storyboard"
            >
              {row}
            </Link>
          );
        }
        return row;
      } },
    { key: "saved_story_ideas", label: "Saved Story Ideas", items: detail.saved_story_ideas, render: (i) => (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-white truncate flex-1">{i.title}</span>
          <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">{i.theme || "—"}</span>
          <WhenPill iso={i.saved_at || i.created_at} />
        </div>
      ) },
    { key: "automation_jobs", label: "Automation Jobs", items: detail.automation_jobs || [], render: (j) => {
        const stepsCount = j.steps_count || 0;
        const total = j.total_scenes || 0;
        const pct = total > 0 ? Math.min(100, Math.round((stepsCount / (total * 4)) * 100)) : 0;
        const statusColor = j.status === "complete" ? "text-emerald-300"
          : j.status === "failed" ? "text-red-300"
          : j.status === "running" ? "text-yellow-400"
          : "text-zinc-400";
        const body = (
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-white truncate flex-1">{j.video_name || j.job_id || "(untitled)"}</span>
              <WhenPill iso={j.updated_at || j.created_at} />
              <span className={`font-mono text-[9px] uppercase tracking-wider shrink-0 ${statusColor}`}>
                {j.status || "—"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 bg-zinc-900 rounded-sm overflow-hidden">
                <div
                  className={`h-full ${j.status === "failed" ? "bg-red-500" : "bg-yellow-400"}`}
                  style={{ width: `${j.status === "complete" ? 100 : pct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                {stepsCount} steps · {total || "?"} scenes
              </span>
            </div>
            {j.last_step && (
              <div className="font-mono text-[10px] text-zinc-500 truncate">
                scene {j.last_step.scene_idx + 1}: {j.last_step.step} → {j.last_step.status}
              </div>
            )}
            {j.error && (
              <div className="text-[10px] text-red-300 truncate" title={j.error}>{j.error}</div>
            )}
            <div className="font-mono text-[9px] text-zinc-600">
              {j.updated_at ? new Date(j.updated_at).toLocaleString() : ""}
            </div>
          </div>
        );
        // Click anywhere on the job row to jump straight into the
        // user's storyboard. We use a `Link` (not navigate) so the
        // admin can also middle-click / Ctrl-click to open in a new
        // tab without disrupting their seat on the Admin page.
        if (j.storyboard_id) {
          return (
            <Link
              to={`/storyboards/${encodeURIComponent(j.storyboard_id)}`}
              data-testid={`admin-job-link-${j.job_id}`}
              className="block hover:bg-zinc-900/60 -mx-2 px-2 py-1 rounded-sm transition-colors"
              title="Open this user's storyboard"
            >
              {body}
            </Link>
          );
        }
        return body;
      } },
  ];
  return (
    <div data-testid={`activity-drill-content-${userId}`} className="mt-3 grid sm:grid-cols-2 gap-4">
      {lists.map((l) => (
        <div key={l.key} className="border border-zinc-900 rounded-sm bg-black p-3">
          <div className="flex items-baseline justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400 font-bold">
              {l.label}
            </div>
            <div className="font-mono text-[10px] text-zinc-500">{l.items.length}</div>
          </div>
          {l.items.length === 0 ? (
            <div className="font-mono text-[11px] text-zinc-600">— none —</div>
          ) : (
            <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
              {l.items.map((it, idx) => (
                <li key={idx} className="border-b border-zinc-900 last:border-b-0 pb-1">
                  {l.render(it)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
